export class StorageEncryptionUtil {
  private static readonly STORAGE_KEY_SUFFIX = '_encrypted';
  private static readonly ALGORITHM = 'AES-GCM';
  private static readonly KEY_LENGTH = 256;

  /**
   * Generate a device-specific encryption key using Web Crypto API
   */
  private static async getEncryptionKey(): Promise<CryptoKey> {
    // Use a combination of factors to create a device-specific key
    const keyMaterial = await this.getKeyMaterial();

    return await window.crypto.subtle.deriveKey(
      {
        name: 'PBKDF2',
        salt: new TextEncoder().encode('patient-portal-salt-v1'),
        iterations: 100000,
        hash: 'SHA-256'
      },
      keyMaterial,
      { name: this.ALGORITHM, length: this.KEY_LENGTH },
      false,
      ['encrypt', 'decrypt']
    );
  }

  /**
   * Get key material based on device-specific factors
   */
  private static async getKeyMaterial(): Promise<CryptoKey> {
    // Create a device-specific identifier
    const deviceId = this.getDeviceIdentifier();

    return await window.crypto.subtle.importKey(
      'raw',
      new TextEncoder().encode(deviceId),
      'PBKDF2',
      false,
      ['deriveKey']
    );
  }

  /**
   * Generate a device-specific identifier
   */
  private static getDeviceIdentifier(): string {
    // Check if we have a stored device ID
    let deviceId = localStorage.getItem('device_id');

    if (!deviceId) {
      // Generate a random device ID and store it
      deviceId = Array.from(window.crypto.getRandomValues(new Uint8Array(32)))
        .map(b => b.toString(16).padStart(2, '0'))
        .join('');
      localStorage.setItem('device_id', deviceId);
    }

    return deviceId;
  }

  /**
   * Encrypt data using AES-GCM
   */
  private static async encrypt(data: string): Promise<string> {
    try {
      const key = await this.getEncryptionKey();
      const iv = window.crypto.getRandomValues(new Uint8Array(12));
      const encodedData = new TextEncoder().encode(data);

      const encryptedData = await window.crypto.subtle.encrypt(
        { name: this.ALGORITHM, iv: iv },
        key,
        encodedData
      );

      // Combine IV and encrypted data
      const combined = new Uint8Array(iv.length + encryptedData.byteLength);
      combined.set(iv, 0);
      combined.set(new Uint8Array(encryptedData), iv.length);

      // Convert to base64
      return btoa(String.fromCharCode(...combined));
    } catch (error) {
      throw error;
    }
  }

  /**
   * Decrypt data using AES-GCM
   */
  private static async decrypt(encryptedData: string): Promise<string> {
    try {
      const key = await this.getEncryptionKey();

      // Decode from base64
      const combined = Uint8Array.from(atob(encryptedData), c => c.charCodeAt(0));

      // Extract IV and encrypted data
      const iv = combined.slice(0, 12);
      const data = combined.slice(12);

      const decryptedData = await window.crypto.subtle.decrypt(
        { name: this.ALGORITHM, iv: iv },
        key,
        data
      );

      return new TextDecoder().decode(decryptedData);
    } catch (error) {
      return '';
    }
  }

  /**
   * Store encrypted data in localStorage with expiry
   */
  static async setItem(key: string, value: any, expiryDays: number): Promise<void> {
    const item = {
      value: value,
      expiry: Date.now() + (expiryDays * 24 * 60 * 60 * 1000)
    };

    try {
      const encrypted = await this.encrypt(JSON.stringify(item));
      localStorage.setItem(key + this.STORAGE_KEY_SUFFIX, encrypted);
    } catch (error) {
    }
  }

  /**
   * Retrieve and decrypt data from localStorage, checking expiry
   */
  static async getItem(key: string): Promise<any> {
    const encrypted = localStorage.getItem(key + this.STORAGE_KEY_SUFFIX);

    if (!encrypted) {
      return null;
    }

    const decrypted = await this.decrypt(encrypted);

    if (!decrypted) {
      localStorage.removeItem(key + this.STORAGE_KEY_SUFFIX);
      return null;
    }

    try {
      const item = JSON.parse(decrypted);

      // Check if expired
      if (Date.now() > item.expiry) {
        localStorage.removeItem(key + this.STORAGE_KEY_SUFFIX);
        return null;
      }

      return item.value;
    } catch (error) {
      localStorage.removeItem(key + this.STORAGE_KEY_SUFFIX);
      return null;
    }
  }

  /**
   * Remove item from localStorage
   */
  static removeItem(key: string): void {
    localStorage.removeItem(key + this.STORAGE_KEY_SUFFIX);
  }
}
